import React from 'react';
import { Triangle, Menu, Search, Shield, AlertTriangle, Lock, Cloud, Server, Database, Check, ArrowRight } from 'lucide-react';
interface LandingPageProps {
  onLogin: () => void;
  onRegister?: () => void;
}
export function LandingPage({
  onLogin,
  onRegister
}: LandingPageProps) {
  return <div className="min-h-screen bg-astro-dark text-white font-sans selection:bg-astro-green selection:text-black" data-id="element-1276">
      {/* A. Landing Navbar */}
      <nav className="sticky top-0 z-50 bg-astro-dark/80 backdrop-blur-md border-b border-astro-border h-16" data-id="element-1277">
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between" data-id="element-1278">
          <div className="flex items-center gap-2" data-id="element-1279">
            <Triangle className="text-astro-green fill-astro-green" size={24} strokeWidth={2.5} data-id="element-1280" />

            <span className="text-xl font-bold text-white tracking-tight" data-id="element-1281">
              Astrolayb
            </span>
          </div>
          <div className="flex items-center gap-4 md:gap-6" data-id="element-1282">
            <a href="https://avistar.ai/" target="_blank" rel="noopener noreferrer" className="hidden md:block text-sm font-medium text-astro-text-secondary hover:text-white transition-colors" data-id="element-1283">
              Avistar.ai
            </a>
            <button onClick={onLogin} className="px-4 py-2 text-sm font-medium text-white border border-astro-border rounded hover:border-astro-green/50 hover:text-astro-green transition-colors" data-id="element-1284">
              Login
            </button>
            <button onClick={onRegister} className="hidden md:block px-4 py-2 text-sm font-bold text-black bg-astro-green rounded hover:bg-astro-green-dark transition-colors shadow-[0_0_10px_rgba(34,197,94,0.2)]" data-id="element-1285">
              Sign Up Free
            </button>
            <button className="md:hidden text-white" data-id="element-1286">
              <Menu size={24} data-id="element-1287" />
            </button>
          </div>
        </div>
      </nav>

      {/* B. Hero Section */}
      <section className="relative pt-20 pb-32 px-6 overflow-hidden" data-id="element-1288">
        {/* Background Star Field Effect */}
        <div className="absolute inset-0 z-0 opacity-30" data-id="element-1289">
          <div className="absolute top-10 left-10 w-1 h-1 bg-white rounded-full animate-pulse" data-id="element-1290"></div>
          <div className="absolute top-40 right-20 w-1.5 h-1.5 bg-astro-green rounded-full opacity-50" data-id="element-1291"></div>
          <div className="absolute bottom-20 left-1/3 w-1 h-1 bg-white rounded-full" data-id="element-1292"></div>
          <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-astro-green/5 rounded-full blur-3xl" style={{
          transform: 'translate(50%, -50%)'
        }} data-id="element-1293"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center" data-id="element-1294">
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 leading-tight" data-id="element-1295">
            Uncover Your Cloud's <br data-id="element-1296" />
            <span className="text-astro-green italic" data-id="element-1297">Blind Spots</span>
          </h1>
          <p className="text-lg md:text-xl text-astro-text-secondary max-w-2xl mx-auto mb-4 leading-relaxed" data-id="element-1298">
            Like ancient astronomers charting the heavens with an astrolabe, we
            illuminate the invisible: service accounts, over-permissioned roles,
            missing MFA, and orphaned identities lurking in your cloud.
          </p>
          <p className="text-sm text-astro-text-secondary/70 mb-10" data-id="element-1299">
            Now scanning AWS, Azure, Azure Entra ID, and on-prem Entra
            environments.
          </p>

          <div className="flex flex-col items-center gap-6" data-id="element-1300">
            <button onClick={onRegister} className="px-8 py-4 bg-astro-green text-black font-bold rounded-lg hover:bg-astro-green-dark transition-colors shadow-[0_0_20px_rgba(34,197,94,0.3)] flex items-center gap-2" data-id="element-1301">
              Start Free Trial <ArrowRight size={18} data-id="element-1302" />
            </button>
            <div className="flex flex-col md:flex-row items-center gap-4 text-xs font-medium text-astro-green" data-id="element-1303">
              <span className="flex items-center gap-2" data-id="element-1304">
                <span className="w-1.5 h-1.5 rounded-full bg-astro-green" data-id="element-1305"></span>
                300+ findings uncovered in a single engagement
              </span>
              <span className="hidden md:block text-astro-text-secondary" data-id="element-1306">
                •
              </span>
              <span className="flex items-center gap-2" data-id="element-1307">
                <span className="w-1.5 h-1.5 rounded-full bg-astro-green" data-id="element-1308"></span>
                MSP-delivered implementation
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* C. Features Section */}
      <section className="py-24 px-6 bg-[#0a0e14]" data-id="element-1309">
        <div className="max-w-7xl mx-auto" data-id="element-1310">
          <div className="text-center mb-16" data-id="element-1311">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-1312">
              Security at{' '}
              <span className="text-astro-green italic" data-id="element-1313">Lightspeed</span>
            </h2>
            <p className="text-astro-text-secondary max-w-2xl mx-auto" data-id="element-1314">
              Built for modern DevSecOps teams who demand speed, precision, and
              uncompromising security.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1315">
            {[{
            icon: <Search size={24} data-id="element-1316" />,
            title: 'Instant Discovery',
            desc: 'Deploy in minutes and immediately scan every service account across AWS and Azure. Zero manual configuration required.'
          }, {
            icon: <Shield size={24} data-id="element-1317" />,
            title: 'Automated Analysis',
            desc: 'Continuously analyze permissions, usage patterns, and access policies to surface security risks across your entire cloud environment.'
          }, {
            icon: <AlertTriangle size={24} data-id="element-1318" />,
            title: 'Prioritized Remediation',
            desc: 'Receive prioritized, actionable recommendations to eliminate over-privileged access and reduce your attack surface.'
          }, {
            icon: <Lock size={24} data-id="element-1319" />,
            title: 'Compliance Ready',
            desc: 'Maintain audit trails and generate compliance reports for SOC 2, ISO 27001, and other security frameworks effortlessly.'
          }].map((feature, i) => <div key={i} className="bg-astro-card border border-astro-border rounded-lg p-8 hover:border-astro-green/30 transition-all group" data-id="element-1320">
                <div className="w-12 h-12 rounded-lg bg-astro-green/10 flex items-center justify-center text-astro-green mb-6 group-hover:scale-110 transition-transform" data-id="element-1321">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3" data-id="element-1322">
                  {feature.title}
                </h3>
                <p className="text-astro-text-secondary leading-relaxed" data-id="element-1323">
                  {feature.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* D. Multi-Cloud Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#0a0e14] to-[#0f1621]" data-id="element-1324">
        <div className="max-w-7xl mx-auto text-center" data-id="element-1325">
          <h2 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-1326">
            Native <span className="text-astro-green" data-id="element-1327">Multi-Cloud</span> Support
          </h2>
          <p className="text-astro-text-secondary max-w-2xl mx-auto mb-16" data-id="element-1328">
            Unified visibility across every major cloud platform with deep,
            native integrations.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-1329">
            {[{
            name: 'AWS',
            sub: 'Amazon Web Services',
            icon: <Cloud size={32} data-id="element-1330" />,
            active: true
          }, {
            name: 'Azure',
            sub: 'Microsoft Azure',
            icon: <Database size={32} data-id="element-1331" />,
            active: true
          }, {
            name: 'GCP',
            sub: 'Google Cloud Platform',
            icon: <Server size={32} data-id="element-1332" />,
            active: false
          }].map(cloud => <div key={cloud.name} className={`bg-astro-card border border-astro-border rounded-xl p-8 flex flex-col items-center ${!cloud.active ? 'opacity-60' : ''}`} data-id="element-1333">
                <div className="relative mb-6" data-id="element-1334">
                  <div className="w-16 h-16 rounded-2xl bg-astro-green/10 flex items-center justify-center text-astro-green" data-id="element-1335">
                    {cloud.icon}
                  </div>
                  {!cloud.active && <span className="absolute -top-3 -right-6 px-2 py-0.5 bg-[#1f2937] border border-gray-700 rounded text-[10px] font-bold uppercase tracking-wider text-gray-400" data-id="element-1336">
                      Coming Soon
                    </span>}
                </div>
                <h3 className="text-2xl font-bold text-white mb-1" data-id="element-1337">
                  {cloud.name}
                </h3>
                <p className="text-sm text-astro-text-secondary mb-6" data-id="element-1338">
                  {cloud.sub}
                </p>
                <ul className="text-left space-y-3 w-full max-w-[200px] mx-auto" data-id="element-1339">
                  {['Complete IAM inventory', 'Real-time sync', 'Zero-trust validation'].map(item => <li key={item} className="flex items-center gap-2 text-xs text-astro-text-secondary" data-id="element-1340">
                      <div className="w-1 h-1 rounded-full bg-astro-green" data-id="element-1341"></div>
                      {item}
                    </li>)}
                </ul>
              </div>)}
          </div>
          <p className="text-sm text-astro-text-secondary" data-id="element-1342">
            More integrations coming soon •{' '}
            <a href="#" className="text-astro-green hover:underline" data-id="element-1343">
              Request your platform
            </a>
          </p>
        </div>
      </section>

      {/* E. Pricing Section */}
      <section className="py-24 px-6 bg-[#0a0e14]" data-id="element-1344">
        <div className="max-w-7xl mx-auto" data-id="element-1345">
          <div className="text-center mb-6" data-id="element-1346">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-1347">
              Identity-Tier <span className="text-astro-green" data-id="element-1348">Pricing</span>
            </h2>
            <p className="text-astro-text-secondary max-w-2xl mx-auto" data-id="element-1349">
              Per-client pricing based on machine identities discovered across
              all connected cloud accounts. Built for MSPs managing multiple
              environments.
            </p>
          </div>

          <p className="text-center text-xs text-astro-text-secondary/70 mb-12 max-w-xl mx-auto" data-id="element-1350">
            Human identities are scanned and included in reports but don't count
            toward pricing. Tiers auto-adjust each billing cycle based on the
            latest scan.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-start" data-id="element-1351">
            {/* Starter */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-7" data-id="element-1352">
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-3" data-id="element-1353">
                Starter
              </div>
              <div className="flex items-baseline gap-1 mb-1" data-id="element-1354">
                <span className="text-3xl font-bold text-white" data-id="element-1355">$499</span>
                <span className="text-sm text-astro-text-secondary" data-id="element-1356">/mo</span>
              </div>
              <p className="text-xs text-astro-text-secondary mb-1" data-id="element-1357">
                per client
              </p>
              <div className="text-[11px] text-gray-500 mb-6 pb-6 border-b border-astro-border" data-id="element-1358">
                Up to <span className="text-white font-medium" data-id="element-1359">250</span>{' '}
                machine identities
              </div>
              <button onClick={onRegister} className="w-full py-2.5 border border-astro-border text-white text-sm font-bold rounded hover:border-astro-green/50 hover:text-astro-green transition-colors mb-6" data-id="element-1360">
                Get Started
              </button>
              <ul className="space-y-3" data-id="element-1361">
                {['Weekly scanning', 'Vulnerability detection', 'Identity inventory', 'Email alerts', 'Standard reports'].map(feature => <li key={feature} className="flex items-start gap-2.5 text-sm text-astro-text-secondary" data-id="element-1362">
                    <Check size={14} className="text-gray-500 mt-0.5 shrink-0" data-id="element-1363" />

                    {feature}
                  </li>)}
              </ul>
            </div>

            {/* Growth — Most Popular */}
            <div className="bg-astro-card border-2 border-blue-500/40 rounded-xl p-7 relative shadow-[0_0_30px_rgba(59,130,246,0.08)]" data-id="element-1364">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-1 bg-blue-500 text-white text-[10px] font-bold uppercase tracking-wider rounded-full" data-id="element-1365">
                Most Popular
              </div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-blue-400 mb-3" data-id="element-1366">
                Growth
              </div>
              <div className="flex items-baseline gap-1 mb-1" data-id="element-1367">
                <span className="text-3xl font-bold text-white" data-id="element-1368">$899</span>
                <span className="text-sm text-astro-text-secondary" data-id="element-1369">/mo</span>
              </div>
              <p className="text-xs text-astro-text-secondary mb-1" data-id="element-1370">
                per client
              </p>
              <div className="text-[11px] text-gray-500 mb-6 pb-6 border-b border-astro-border" data-id="element-1371">
                <span className="text-white font-medium" data-id="element-1372">251–1,000</span>{' '}
                machine identities
              </div>
              <button onClick={onRegister} className="w-full py-2.5 bg-blue-500 text-white text-sm font-bold rounded hover:bg-blue-600 transition-colors mb-6 shadow-lg shadow-blue-500/20" data-id="element-1373">
                Get Started
              </button>
              <ul className="space-y-3" data-id="element-1374">
                {['2× per week scanning', 'Automated analysis', 'Full identity inventory', 'Prioritized remediation', 'Compliance reports', 'Multi-cloud dashboards'].map(feature => <li key={feature} className="flex items-start gap-2.5 text-sm text-white" data-id="element-1375">
                    <Check size={14} className="text-blue-400 mt-0.5 shrink-0" data-id="element-1376" />

                    {feature}
                  </li>)}
              </ul>
            </div>

            {/* Enterprise */}
            <div className="bg-astro-card border-2 border-astro-green/30 rounded-xl p-7 relative shadow-[0_0_30px_rgba(34,197,94,0.08)]" data-id="element-1377">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-1 bg-astro-green text-black text-[10px] font-bold uppercase tracking-wider rounded-full" data-id="element-1378">
                Best Value
              </div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-astro-green mb-3" data-id="element-1379">
                Enterprise
              </div>
              <div className="flex items-baseline gap-1 mb-1" data-id="element-1380">
                <span className="text-3xl font-bold text-white" data-id="element-1381">$1,499</span>
                <span className="text-sm text-astro-text-secondary" data-id="element-1382">/mo</span>
              </div>
              <p className="text-xs text-astro-text-secondary mb-1" data-id="element-1383">
                per client
              </p>
              <div className="text-[11px] text-gray-500 mb-6 pb-6 border-b border-astro-border" data-id="element-1384">
                <span className="text-white font-medium" data-id="element-1385">1,001–5,000</span>{' '}
                machine identities
              </div>
              <button onClick={onRegister} className="w-full py-2.5 bg-astro-green text-black text-sm font-bold rounded hover:bg-astro-green-dark transition-colors mb-6 shadow-lg shadow-astro-green/20" data-id="element-1386">
                Get Started
              </button>
              <ul className="space-y-3" data-id="element-1387">
                {['Daily scanning', 'Everything in Growth', 'Priority support', 'Advanced remediation', 'Insurance-grade reports', 'Loss exposure estimates', 'Custom integrations'].map(feature => <li key={feature} className="flex items-start gap-2.5 text-sm text-white" data-id="element-1388">
                    <Check size={14} className="text-astro-green mt-0.5 shrink-0" data-id="element-1389" />

                    {feature}
                  </li>)}
              </ul>
            </div>

            {/* Custom */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-7" data-id="element-1390">
              <div className="text-[10px] font-bold uppercase tracking-wider text-yellow-400 mb-3" data-id="element-1391">
                Custom
              </div>
              <div className="flex items-baseline gap-1 mb-1" data-id="element-1392">
                <span className="text-3xl font-bold text-white" data-id="element-1393">Contact</span>
              </div>
              <p className="text-xs text-astro-text-secondary mb-1" data-id="element-1394">
                custom pricing
              </p>
              <div className="text-[11px] text-gray-500 mb-6 pb-6 border-b border-astro-border" data-id="element-1395">
                <span className="text-white font-medium" data-id="element-1396">5,000+</span> machine
                identities
              </div>
              <button onClick={onRegister} className="w-full py-2.5 border border-astro-border text-white text-sm font-bold rounded hover:border-yellow-400/50 hover:text-yellow-400 transition-colors mb-6" data-id="element-1397">
                Contact Sales
              </button>
              <ul className="space-y-3" data-id="element-1398">
                {['Up to continuous scanning', 'Everything in Enterprise', 'Dedicated account manager', 'SLA guarantees', 'On-prem deployment', 'Custom compliance frameworks', 'White-label reporting'].map(feature => <li key={feature} className="flex items-start gap-2.5 text-sm text-astro-text-secondary" data-id="element-1399">
                    <Check size={14} className="text-yellow-400 mt-0.5 shrink-0" data-id="element-1400" />

                    {feature}
                  </li>)}
              </ul>
            </div>
          </div>

          {/* Volume Discounts */}
          <div className="mt-12 p-6 bg-astro-card border border-astro-border rounded-xl max-w-3xl mx-auto" data-id="element-1401">
            <h3 className="text-sm font-bold text-white text-center mb-4" data-id="element-1402">
              MSP Volume Discounts
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1403">
              {[{
              range: '1–10 clients',
              discount: 'Standard',
              color: 'text-astro-text-secondary'
            }, {
              range: '11–25 clients',
              discount: '10% off',
              color: 'text-astro-green'
            }, {
              range: '26–50 clients',
              discount: '15% off',
              color: 'text-astro-green'
            }, {
              range: '50+ clients',
              discount: 'Custom',
              color: 'text-yellow-400'
            }].map(tier => <div key={tier.range} className="text-center p-3 bg-[#0a0e14] rounded-lg border border-astro-border" data-id="element-1404">
                  <div className={`text-sm font-bold ${tier.color}`} data-id="element-1405">
                    {tier.discount}
                  </div>
                  <div className="text-[11px] text-astro-text-secondary mt-1" data-id="element-1406">
                    {tier.range}
                  </div>
                </div>)}
            </div>
            <p className="text-[11px] text-astro-text-secondary text-center mt-4" data-id="element-1407">
              Discounts apply across all tiers. The more clients you manage, the
              more you save.
            </p>
          </div>
        </div>
      </section>

      {/* F. Bottom CTA */}
      <section className="py-24 px-6 border-t border-astro-border bg-[#0a0e14]" data-id="element-1408">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1409">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1410">
            Ready to Illuminate Your <br data-id="element-1411" />
            <span className="text-astro-green italic" data-id="element-1412">Cloud Blind Spots?</span>
          </h2>
          <p className="text-lg text-astro-text-secondary mb-10 max-w-2xl mx-auto" data-id="element-1413">
            Partner with our certified MSPs for expert implementation and
            ongoing security management.
          </p>
          <button onClick={onRegister} className="px-8 py-4 border border-white text-white font-bold rounded-lg hover:bg-white hover:text-black transition-colors mb-10" data-id="element-1414">
            Schedule a Consultation
          </button>
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 text-sm text-astro-text-secondary" data-id="element-1415">
            <span className="flex items-center gap-2" data-id="element-1416">
              <Check size={16} className="text-astro-green" data-id="element-1417" /> Expert MSP
              implementation
            </span>
            <span className="flex items-center gap-2" data-id="element-1418">
              <Check size={16} className="text-astro-green" data-id="element-1419" /> Tailored to your
              environment
            </span>
            <span className="flex items-center gap-2" data-id="element-1420">
              <Check size={16} className="text-astro-green" data-id="element-1421" /> Ongoing support
            </span>
          </div>
        </div>
      </section>

      {/* G. Footer */}
      <footer className="py-8 px-6 border-t border-astro-border bg-[#05070a]" data-id="element-1422">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-1423">
          <div className="flex items-center gap-2" data-id="element-1424">
            <Triangle className="text-astro-green fill-astro-green" size={20} strokeWidth={2.5} data-id="element-1425" />

            <span className="text-lg font-bold text-white tracking-tight" data-id="element-1426">
              Astrolayb
            </span>
            <span className="text-xs text-astro-text-secondary ml-2" data-id="element-1427">
              by Avistar.AI
            </span>
          </div>
          <div className="text-xs text-astro-text-secondary" data-id="element-1428">
            © 2026 Avistar.AI. All rights reserved.
          </div>
        </div>
      </footer>
    </div>;
}